import { Search } from "@deere/fuel-react";
import { Dispatch, Ref, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  OrdersByLineItemActions,
  setFilterAction,
} from "../../reducers/actions";
import { OrdersByLineItemFilter } from "../../types";

type OrdersByLineItemSearchFilterProps = {
  inputRef: Ref<any>;
  field: keyof OrdersByLineItemFilter;
  filters: OrdersByLineItemFilter;
  dispatch: Dispatch<OrdersByLineItemActions>;
  onFilterChange?: (filters: OrdersByLineItemFilter) => void;
};

const OrdersByLineItemSearchFilter = ({
  inputRef,
  field,
  filters,
  dispatch,
  onFilterChange,
}: OrdersByLineItemSearchFilterProps) => {
  const [filterTerm, setFilterTerm] = useState(filters[field] ?? "");
  const [startFilter, setStartFilter] = useState(false);
  const filterTermRef = useRef(filterTerm);
  const { t } = useTranslation("orders");

  useEffect(() => {
    filterTermRef.current = filterTerm;
  }, [filterTerm]);

  useEffect(() => {
    const nextValue = (filters[field] as string | undefined) ?? "";

    if (nextValue !== filterTermRef.current) {
      filterTermRef.current = nextValue;
      setFilterTerm(nextValue);
    }
  }, [field, filters]);

  useEffect(() => {
    if (!startFilter) return undefined;

    const timeoutId = setTimeout(() => {
      const updatedFilters =
        filterTermRef.current === ""
          ? { ...filters, [field]: undefined }
          : { ...filters, [field]: filterTermRef.current };

      if (onFilterChange) {
        onFilterChange(updatedFilters);
      } else {
        dispatch(setFilterAction(updatedFilters));
      }
      setStartFilter(false);
    }, 1000);

    return () => clearTimeout(timeoutId);
  }, [startFilter]);

  return (
    <Search
      data-testid={`orders-by-line-item-filter-${field}`}
      placeholder={t("filter.search.placeholder") as string}
      inputProps={{
        ref: inputRef,
      }}
      sx={{ ".MuiOutlinedInput-input": { textOverflow: "ellipsis" } }}
      value={filterTerm}
      onChange={(event) => {
        setFilterTerm(event.target.value);
        setStartFilter(true);
      }}
      onClear={() => {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { [field]: _, ...updatedFilters } = filters;
        if (onFilterChange) {
          onFilterChange(updatedFilters);
        } else {
          dispatch(setFilterAction(updatedFilters));
        }
        setFilterTerm("");
      }}
      onKeyDown={(event) => {
        if (event.key === "Enter") {
          event.preventDefault();
        }
      }}
    />
  );
};

export default OrdersByLineItemSearchFilter;
